import React, {useCallback, useEffect, useRef, useState} from "react";
import {useEntityLoader, UseEntityLoaderProps} from "../../hooks/entites/useEntityLoader";
import EntityForm, {EntityFormProps} from "./EntityForm/EntityForm";
import {isErrorResponse} from "../../utils/utils";
import {useToastContext} from "../../context/toastContext";
import {useTranslation} from "react-i18next";
import {BaseEntity, BaseEntityNameLess} from "../../api/entities/BaseEntity";
import {useDispatch} from "react-redux";
import UndoLastOperationButton
    from "../../project-modes/RoadNetworkMode/RoadNetworkManager/UndoLastOperationButton/UndoLastOperationButton";
import {setSourceReloadRequired} from "../../redux/map/map-reducer";
import {useBlocker, useLocation} from "react-router-dom";
import {LoadingBackdrop} from "../LoadingBackdrop/LoadingBackdrop";
import {usePresetsContext} from "../../context/presetsContext";
import {useLayersInteractivity} from "../../hooks/map/useLayersInteractivity";
import {FormDataObj} from "../../utils/types";
import {useNavigatePreserveSearch} from "../../hooks/useNavigatePreserveSearch";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";


//TODO loadByDefault is to avoid entity load in TransitRouteEdit => update architecture
type Props = {
    entityId: string;
    entityName: string;
    layerId: string;
    onSaveFn?: (resp: any) => Promise<void> | void;
    getUrlOnSaveFn?: (resp: any) => string | undefined | Promise<string | undefined>;
    getUrlOnDeleteFn?: () => string;
    actionButtons?: JSX.Element | JSX.Element[];
    undoLastActionBtn?: boolean;
    loadByDefault?: boolean;
} & UseEntityLoaderProps
    & Pick<EntityFormProps, 'showDeleteBtn'>;

const EntityEditor = <T extends BaseEntityNameLess>({
                                                        entityId,
                                                        entityName,
                                                        layerId,
                                                        saveFnArgs,
                                                        presetProperty,
                                                        onSaveFn,
                                                        getUrlOnSaveFn,
                                                        getUrlOnDeleteFn,
                                                        actionButtons,
                                                        undoLastActionBtn = false,
                                                        entityView,
                                                        loadByDefault = true,
                                                        showDeleteBtn,
                                                    }: Props) => {
    const {selectedPresetId} = usePresetsContext();
    const presetIdOnMount = useRef<string>('');
    const {
        loading,
        entity,
        unsavedEntityChanges,
        metadata,
        loadData,
        changeForm,
        saveFn,
        deleteFn
    } = useEntityLoader<T>({entityName, saveFnArgs, presetProperty});
    const {setDrawing} = useLayersInteractivity();
    const {addToast} = useToastContext();
    const {t} = useTranslation();
    const location = useLocation();
    const [ignoreBlocker, setIgnoreBlocker] = useState(false);
    const navigationBlocker = useBlocker(
        ({currentLocation, nextLocation}) =>
            !ignoreBlocker &&
            unsavedEntityChanges &&
            currentLocation.pathname !== nextLocation.pathname
    );
    const navigate = useNavigatePreserveSearch();
    const dispatch = useDispatch();

    const updatedEntityId = entityId === 'new' ? null : entityId;

    useEffect(() => {
        if (loadByDefault) {
            void (async function () {
                //TODO redirect to list or project if entity doesn't exist
                await loadData({entityName, entityId: updatedEntityId, entityView});
            })();
        }
    }, [entityName, updatedEntityId, entityView, loadByDefault]);

    const saveEntity = useCallback(async (formDataObj: FormDataObj) => {
        const abortController = new AbortController();

        const resp = await saveFn({
            id: updatedEntityId,
            entityName,
            data: formDataObj,
            abortSignal: abortController.signal
        });

        if (isErrorResponse(resp)) {
            addToast(`${resp.error_description}`, 'error');
        } else {
            addToast(t('common.save-successful'), 'success');
            await loadData({entityName, entityId: updatedEntityId, entityView});
            if (layerId) {
                dispatch(setSourceReloadRequired({sourceId: layerId, isReloadRequired: true}));
            }
            setDrawing(false);

            await onSaveFn?.(resp);

            if (getUrlOnSaveFn) {
                const url = await getUrlOnSaveFn?.(resp);
                if (url) {
                    navigationBlocker?.reset?.();
                    navigate(url, {state: {prevUrl: location.pathname}, relative: "path"});
                }
            }
        }

    }, [entityName, saveFn, updatedEntityId, layerId, getUrlOnSaveFn, setDrawing, navigationBlocker]);

    const navigateToRequiredUrl = useCallback((getUrlOnDeleteFn: () => string) => {
        const url = getUrlOnDeleteFn();
        if (url) {
            navigate(url, {state: {prevUrl: location.pathname}});
        }
    }, [])

    const deleteEntity = useCallback(async ({entity}: { entity: T }) => {
        const resp = await deleteFn?.({id: entity.id});

        if (isErrorResponse(resp)) {
            addToast(`${resp.error_description}`, 'error');
        } else {
            addToast(t('common.delete-successful'), 'success');

            if (layerId) {
                dispatch(setSourceReloadRequired({sourceId: layerId, isReloadRequired: true}));
            }

            if (getUrlOnDeleteFn) {
                navigationBlocker?.reset?.();
                navigateToRequiredUrl(getUrlOnDeleteFn);
            }
        }
    }, [layerId, navigateToRequiredUrl, navigationBlocker])

    const onLastActionUndo = useCallback(async () => {
        await loadData({entityName, entityId: updatedEntityId});
    }, [loadData, updatedEntityId, entityName]);

    useEffect(() => {
        if (ignoreBlocker && getUrlOnDeleteFn) {
            navigateToRequiredUrl(getUrlOnDeleteFn);
            setIgnoreBlocker(false);
        }
    }, [ignoreBlocker, getUrlOnDeleteFn]);

    useEffect(() => {
        if (presetIdOnMount.current && getUrlOnDeleteFn) {
            setIgnoreBlocker(true);
        }
        presetIdOnMount.current = selectedPresetId;
    }, [selectedPresetId, getUrlOnDeleteFn, navigateToRequiredUrl, setIgnoreBlocker]);

    const confirmNavigation = useCallback(() => {
        navigationBlocker?.proceed?.();
    }, [navigationBlocker])

    const cancelNavigation = useCallback(() => {
        navigationBlocker?.reset?.();
    }, [navigationBlocker])

    return (
        <LoadingBackdrop isVisible={loading}>
            <EntityForm
                loading={loading}
                entity={entity}
                metadata={metadata}
                changeFormFn={changeForm}
                saveFn={saveEntity}
                deleteModalTitle={t('common.delete-confirmation')}
                deleteFn={deleteEntity as any}
                icons={
                    <>
                        {actionButtons}
                        {
                            undoLastActionBtn
                                ? <UndoLastOperationButton onFinishFn={onLastActionUndo}/>
                                : null
                        }
                    </>
                }
                showDeleteBtn={showDeleteBtn}
            />
            {navigationBlocker.state === "blocked" ? (
                <ConfirmationModal
                    title={t("common.confirm-leaving-action-with-unsaved-changes")}
                    setModalShow={cancelNavigation}
                    onConfirmFn={confirmNavigation}
                    onCancelFn={cancelNavigation}
                />
            ) : null}
        </LoadingBackdrop>
    )
}

export default EntityEditor;